/* bs-testimonials — custom element con carrusel scroll-snap + dots + nav + IntersectionObserver.
   Features:
   - Click nav prev/next → scroll por 1 card-width + gap.
   - Click dot → scroll smooth a la card index.
   - IntersectionObserver sobre cards → auto-update dot activo + counter "X / Y".
   - Disable nav buttons cuando inicio/final del scroll.
   - prefers-reduced-motion respect.
   - shopify:section:load re-init defensivo (customizer).
*/

(function () {
  'use strict';

  class BsTestimonials extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.track = this.querySelector('[data-bs-testimonials-track]');
      if (!this.track) return;

      this.cards = Array.from(this.querySelectorAll('[data-bs-testimonials-card]'));
      this.totalCards = this.cards.length;
      if (this.totalCards === 0) return;

      this.prevBtns = Array.from(this.querySelectorAll('[data-direction="prev"]'));
      this.nextBtns = Array.from(this.querySelectorAll('[data-direction="next"]'));
      this.dotsContainer = this.querySelector('[data-bs-testimonials-dots]');
      this.dots = Array.from(this.querySelectorAll('[data-bs-testimonials-dot]'));
      this.counterCurrent = this.querySelector('[data-bs-testimonials-current]');
      this.counterTotal = this.querySelector('[data-bs-testimonials-total]');
      this.counterWrap = this.querySelector('[data-bs-testimonials-counter]');

      // activePage = pagina actual (0..totalPages-1). Mobile: 2 cards/pagina. Desktop: 3.
      this.activePage = 0;
      this.cardsPerPage = 1;
      this.totalPages = 1;

      this.recalcPagination();
      this.regenerateDots();
      this.bindEvents();
      this.updateUi();
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      if (this._onScroll && this.track) {
        this.track.removeEventListener('scroll', this._onScroll);
        this._onScroll = null;
      }
      this._initialized = false;
    }

    bindEvents() {
      this.prevBtns.forEach((btn) => btn.addEventListener('click', () => this.go(-1)));
      this.nextBtns.forEach((btn) => btn.addEventListener('click', () => this.go(1)));

      // Resize → recalcular pagination (cards por pagina puede cambiar) + regenerar dots.
      this._onResize = () => {
        const prevPages = this.totalPages;
        this.recalcPagination();
        if (this.totalPages !== prevPages) {
          this.regenerateDots();
        }
        // Re-clamp activePage si totalPages bajo
        if (this.activePage >= this.totalPages) {
          this.activePage = Math.max(0, this.totalPages - 1);
        }
        this.updateUi();
      };
      window.addEventListener('resize', this._onResize, { passive: true });

      // Scroll listener: detecta swipe manual y sincroniza activePage. Debounce con rAF.
      this._scrollRaf = null;
      this._onScroll = () => {
        if (this._scrollRaf) return;
        this._scrollRaf = requestAnimationFrame(() => {
          this._scrollRaf = null;
          this.syncPageFromScroll();
        });
      };
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
    }

    /**
     * Calcula cardsPerPage segun cuantas cards caben en clientWidth del track.
     * Formula: floor((clientWidth + gap) / (cardWidth + gap)). +gap en numerador
     * para que la ultima card sin gap trailing tambien cuente.
     */
    recalcPagination() {
      if (!this.cards.length) {
        this.cardsPerPage = 1;
        this.totalPages = 1;
        this.step = 0;
        return;
      }
      const cardWidth = this.cards[0].offsetWidth;
      let gap = 0;
      const computed = window.getComputedStyle(this.track);
      const parsedGap = parseFloat(computed.gap || computed.columnGap);
      if (!Number.isNaN(parsedGap)) gap = parsedGap;
      const clientWidth = this.track.clientWidth;
      const slot = cardWidth + gap;
      this.cardsPerPage = Math.max(1, Math.floor((clientWidth + gap) / slot));
      this.totalPages = Math.max(1, Math.ceil(this.totalCards / this.cardsPerPage));
      this.step = slot;
    }

    regenerateDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      if (this.totalPages <= 1) {
        this.dotsContainer.style.display = 'none';
        this.dots = [];
        return;
      }
      this.dotsContainer.style.display = '';
      for (let i = 0; i < this.totalPages; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bs-testimonials__dot' + (i === this.activePage ? ' bs-testimonials__dot--active' : '');
        btn.dataset.bsTestimonialsDot = String(i);
        btn.setAttribute('aria-label', 'Ir a la página ' + (i + 1) + ' de ' + this.totalPages);
        if (i === this.activePage) btn.setAttribute('aria-current', 'true');
        btn.addEventListener('click', () => this.goTo(i));
        this.dotsContainer.appendChild(btn);
      }
      this.dots = Array.from(this.dotsContainer.querySelectorAll('[data-bs-testimonials-dot]'));
    }

    syncPageFromScroll() {
      if (this.step <= 0 || this.cardsPerPage <= 0) return;
      const pageWidth = this.cardsPerPage * this.step;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const scrollLeft = this.track.scrollLeft;
      let page;
      // Si scroll esta al final (maxScroll real < ultimo target teorico porque la ultima
      // pagina tiene menos cards), asignar ultima pagina. Tolerancia 2px por subpixel.
      if (maxScroll > 0 && scrollLeft >= maxScroll - 2) {
        page = this.totalPages - 1;
      } else {
        page = Math.round(scrollLeft / pageWidth);
      }
      const clamped = Math.max(0, Math.min(page, this.totalPages - 1));
      if (clamped !== this.activePage) {
        this.activePage = clamped;
        this.updateUi();
      } else {
        this.updateNavState();
      }
    }

    go(direction) {
      const newPage = direction > 0
        ? Math.min(this.activePage + 1, this.totalPages - 1)
        : Math.max(this.activePage - 1, 0);
      this.goTo(newPage);
    }

    goTo(pageIdx) {
      const clamped = Math.max(0, Math.min(pageIdx, this.totalPages - 1));
      this.activePage = clamped;
      if (this.step > 0 && this.cardsPerPage > 0) {
        const behavior = this.prefersReduced() ? 'auto' : 'smooth';
        let target;
        if (clamped >= this.totalPages - 1) {
          // Ultima pagina: scroll al maxScroll para mostrar las cards restantes alineadas
          // al final del track. Evita que el browser cap a maxScroll y syncPageFromScroll
          // calcule page incorrecto (causaba "doble click next" cuando hay menos cards
          // que cardsPerPage en la ultima pagina).
          target = this.track.scrollWidth - this.track.clientWidth;
        } else {
          target = clamped * this.cardsPerPage * this.step;
        }
        this.track.scrollTo({ left: Math.max(0, target), behavior });
      }
      this.updateUi();
    }

    prefersReduced() {
      return (
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    }

    updateNavState() {
      const prevDisabled = this.activePage <= 0;
      const nextDisabled = this.activePage >= this.totalPages - 1;
      this.prevBtns.forEach((btn) => {
        btn.disabled = prevDisabled;
        btn.setAttribute('aria-disabled', prevDisabled ? 'true' : 'false');
      });
      this.nextBtns.forEach((btn) => {
        btn.disabled = nextDisabled;
        btn.setAttribute('aria-disabled', nextDisabled ? 'true' : 'false');
      });
    }

    updateUi() {
      this.updateNavState();

      this.dots.forEach((dot, i) => {
        const isActive = i === this.activePage;
        dot.classList.toggle('bs-testimonials__dot--active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.counterCurrent) {
        this.counterCurrent.textContent = String(this.activePage + 1);
      }
      if (this.counterTotal) {
        this.counterTotal.textContent = String(this.totalPages);
      }
      if (this.counterWrap) {
        this.counterWrap.setAttribute(
          'aria-label',
          'Página ' + (this.activePage + 1) + ' de ' + this.totalPages
        );
      }
    }
  }

  if (!customElements.get('bs-testimonials')) {
    customElements.define('bs-testimonials', BsTestimonials);
  }

  // Re-init defensivo cuando el customizer recarga la section
  document.addEventListener('shopify:section:load', (event) => {
    const el = event.target.querySelector('bs-testimonials');
    if (el && typeof el.connectedCallback === 'function') {
      el._initialized = false;
      el.connectedCallback();
    }
  });
})();
